import { flagAllows, flagPending } from "./flag-gate.ts";
import type { FlagValues, NavigationEntry, ScreenDeclaration } from "./web-module.ts";

// S12.01: what a screen or a navigation entry needs before it draws: its flag (ADR-006 §7) and
// its permission (`requiresPermission`). Both resolve the same way: nothing while the value is
// still loading, the not-found screen (or no entry) once it has loaded without it. Pure, so the
// shell's filter and a screen's gate are unit tested without rendering (`createUi` is the only
// caller at runtime).

/** What the router knows when it resolves a screen: the active organization's flags and the
 * session's permissions, each undefined until it has loaded. */
export type AccessValues = {
  readonly flags: FlagValues | undefined;
  readonly permissions: readonly string[] | undefined;
};

/** An entry with no requirement is always allowed; one naming a permission only once the
 * session's permissions hold it (unknown counts as not held). */
export function permissionAllows(
  permission: string | undefined,
  permissions: readonly string[] | undefined,
): boolean {
  if (permission === undefined) return true;
  return permissions?.includes(permission) === true;
}

/** True while a gated screen's permissions have not arrived: draw nothing, not even the
 * not-found screen, so a slow session read never flashes either state. */
export function permissionPending(
  permission: string | undefined,
  permissions: readonly string[] | undefined,
): boolean {
  return permission !== undefined && permissions === undefined;
}

export type ScreenGate = "pending" | "allowed" | "refused";

/** How the router treats a matched screen: draw nothing yet, draw it, or draw the not-found
 * screen in its place, exactly like a path no module declared. */
export function screenGate(screen: ScreenDeclaration, values: AccessValues): ScreenGate {
  if (flagPending(screen.flag, values.flags)) return "pending";
  if (permissionPending(screen.requiresPermission, values.permissions)) return "pending";
  const allowed =
    flagAllows(screen.flag, values.flags) && permissionAllows(screen.requiresPermission, values.permissions);
  return allowed ? "allowed" : "refused";
}

/** Every entry whose flag is on and whose permission is held, or that names neither; nothing
 * gated before its value arrives. */
export function allowedNavigation<TEntry extends NavigationEntry>(
  entries: readonly TEntry[],
  values: AccessValues,
): readonly TEntry[] {
  return entries.filter(
    (entry) =>
      flagAllows(entry.flag, values.flags) && permissionAllows(entry.requiresPermission, values.permissions),
  );
}
